import {
  CHART_LIMITS,
  HEX_COLOUR_RE,
  REQUIRED_PALETTE_KEYS,
  type BaseChart,
  type ChartValidationIssue,
  type ChartValidationResult,
} from './chart.js';
import { VARIANTS, type Variant } from './gameTypes.js';
import {
  decodeJumpGrid,
  jumpSpawnTopY,
  JUMP_CELL_PX,
  JUMP_GRID_ROWS,
  JUMP_HINT_MAX_LENGTH,
  JUMP_MAX_GRID_COLS,
  JUMP_TILE,
  type JumpGrid,
} from './jumpGrid.js';

/**
 * Jump mode — a Geometry-Dash-cube / Impossible-Game style one-button runner.
 * A cube auto-scrolls right at a constant, beat-locked speed; the player taps
 * (short hop) or holds (full jump) in time to the music to clear ground spikes,
 * mount raised terrain, leap pits, duck overhead corridors, and thread floating
 * saws. Failure is one-shot — any collision, or falling into a pit, ends the run.
 *
 * The map is a beat-aligned tile grid ({@link JumpGrid}) — a column is a slice
 * of beat-time, a row a band of playfield Y — which compiles deterministically
 * into the engine's surface / hazard / ceiling model. Like the other modes the
 * builtin grids are derived at module load from the shared Circles osu JSON, so
 * obstacles still land on real audio onsets, and the server re-derives the same
 * engine config from the stored grid for replay validation.
 */
export const JUMP_PLAYFIELD_HEIGHT = JUMP_GRID_ROWS * JUMP_CELL_PX;

/** Width of the Jump playfield (kept at the historical 4:3-ish framing). */
export const JUMP_PLAYFIELD_WIDTH = 960;

/**
 * Visual ceiling of the playfield (a render bound, not a lethal surface). The
 * jump arc never reaches it — one full jump tops out ~150 units above the
 * ground.
 */
export const JUMP_CEILING_Y = 40;

/** Jump-mode chart payload — the map lives entirely in {@link grid}. */
export interface JumpChart extends BaseChart {
  mode: 'jump';
  /**
   * Downward gravitational acceleration in playfield units per beat². The
   * cube's airborne Y follows `y0 + vy0·t + ½·gravity·t²` (t in beats).
   */
  gravity: number;
  /**
   * Upward launch speed (units per beat) of a FULL (long) jump. With
   * {@link gravity} this fixes the long jump's air-time and peak height. A held
   * press flies the full arc; a quick tap is cut to {@link shortJumpVelocity}.
   */
  jumpVelocity: number;
  /**
   * Upward launch speed of a SHORT (tapped) jump (< {@link jumpVelocity}). An
   * early release while ascending clamps the cube onto the short arc, giving a
   * crisp fixed short apex for map design.
   */
  shortJumpVelocity: number;
  /** Side length of the (square) cube in playfield units. Half is the hitbox reach. */
  playerSize: number;
  /** The beat-aligned tile grid that defines the level. */
  grid: JumpGrid;
}

/**
 * Per-variant gameplay tuning. The jump arc (`gravity`, `jumpVelocity`,
 * `shortJumpVelocity`) is CONSTANT across variants — together with the constant
 * scroll runway this means both arcs have an identical on-screen footprint on
 * every tier and song, so players build one set of muscle memory. Difficulty
 * comes from obstacle density (`minGapBeats`, smaller = more obstacles survive),
 * hitbox size (`playerSize`, smaller = less forgiving), and how wide a spike
 * CLUSTER each tier may present (`maxClusterCols`, in grid columns).
 * `minGapBeats` must stay ≥ the air-time so the cube always lands between
 * required jumps.
 */
export const JUMP_VARIANT_TUNING: Record<
  Variant,
  {
    gravity: number;
    jumpVelocity: number;
    shortJumpVelocity: number;
    playerSize: number;
    minGapBeats: number;
    maxClusterCols: number;
  }
> = {
  normal: { gravity: 1480, jumpVelocity: 666, shortJumpVelocity: 470, playerSize: 30, minGapBeats: 1.35, maxClusterCols: 2 },
  hard: { gravity: 1480, jumpVelocity: 666, shortJumpVelocity: 470, playerSize: 28, minGapBeats: 1.1, maxClusterCols: 3 },
  extreme: { gravity: 1480, jumpVelocity: 666, shortJumpVelocity: 470, playerSize: 26, minGapBeats: 0.95, maxClusterCols: 4 },
};

/** Air-time of a single jump in beats (`2·jumpVelocity / gravity`). */
export function jumpAirtimeBeats(gravity: number, jumpVelocity: number): number {
  return (2 * jumpVelocity) / gravity;
}

/** Beats from launch to the apex (`jumpVelocity / gravity`) — half the air-time. */
export function jumpApexLeadBeats(gravity: number, jumpVelocity: number): number {
  return jumpVelocity / gravity;
}

/** Peak height of a jump above the launch surface (`jumpVelocity² / (2·gravity)`). */
export function jumpPeakHeight(gravity: number, jumpVelocity: number): number {
  return (jumpVelocity * jumpVelocity) / (2 * gravity);
}

/**
 * Jump input-buffer window in BEATS for a chart: {@link JUMP_INPUT_BUFFER_MS}
 * expressed in this chart's beat space, capped at
 * {@link JUMP_INPUT_BUFFER_MAX_AIRTIME_FRAC} of the air-time.
 */
export function jumpInputBufferBeats(bpm: number, gravity: number, jumpVelocity: number): number {
  const fromMs = (JUMP_INPUT_BUFFER_MS / 1000) * (bpm / 60);
  const airtimeCap = jumpAirtimeBeats(gravity, jumpVelocity) * JUMP_INPUT_BUFFER_MAX_AIRTIME_FRAC;
  return Math.min(fromMs, airtimeCap);
}

/**
 * The "perfect zone" — a BAND of vertical clearance (units the cube's bottom
 * edge sits above an obstacle's top) within which a pass grades `perfect`.
 * Unlike a one-sided "clear by ≥ N" threshold, this is a WINDOW: too LITTLE
 * clearance (a scrappy, mistimed graze) AND too MUCH (a needlessly big jump
 * that sails way over the top) both grade `good`. The cube's parabolic arc only
 * threads the band when the jump is the right SIZE and lands its apex over the
 * obstacle on the beat — so a `perfect` demands matching the small/big jump to
 * the obstacle's height AND timing the press to the music, rather than just
 * holding to clear everything.
 *
 * The builtin spike heights are chosen so each obstacle's INTENDED jump apexes
 * squarely inside this band: a 1-cell spike under the short hop and a 3-cell
 * spike under the full jump both crest at ≈ 40 units of clearance. Because that
 * apex clearance is `peakHeight − spikeHeight` (independent of `playerSize`),
 * one fixed band serves every variant. The WRONG jump misses it — a full jump
 * over a low spike overshoots (clearance ≫ max → `good`) and a short hop can't
 * reach over a tall spike at all. See `jumpFromCircles.ts` for the derivation.
 */
export const JUMP_PERFECT_MIN_CLEARANCE = 24;
export const JUMP_PERFECT_MAX_CLEARANCE = 60;

/**
 * Grade a hazard pass from where the cube's bottom-edge clearance landed
 * relative to the perfect zone. Shared by the engine (live + replay scoring)
 * and the renderer (drawing the zone), so visuals and scoring never drift.
 */
export function gradeJumpClearance(clearance: number): 'perfect' | 'good' {
  return clearance >= JUMP_PERFECT_MIN_CLEARANCE && clearance <= JUMP_PERFECT_MAX_CLEARANCE
    ? 'perfect'
    : 'good';
}

/**
 * Fraction of the cube's half-extent the SPIKE / SAW kill-test forgives — a
 * Geometry-Dash "hitbox smaller than the sprite" leniency, so clipping a tip on
 * an early/late jump survives. Solid terrain leading-faces keep the full body.
 */
export const JUMP_SPIKE_FORGIVENESS_FRAC = 0.4;

/** Ceiling (overhang) kill-test forgiveness — the upper-edge analogue. */
export const JUMP_CEILING_FORGIVENESS_FRAC = 0.4;

/** Jump input buffer in real milliseconds (GD/Impossible "jump buffering"). */
export const JUMP_INPUT_BUFFER_MS = 90;

/** Fraction of the air-time the input buffer is capped at. */
export const JUMP_INPUT_BUFFER_MAX_AIRTIME_FRAC = 0.35;

/**
 * Horizontal scroll-speed dial. Visual only and uniform across variants and
 * tempo: it sets the constant obstacle runway (`JUMP_VISIBLE_BEATS_AHEAD =
 * 11 / this`) and hence a fixed playfield-units-per-beat scroll, so every jump
 * has the same on-screen footprint.
 */
export const JUMP_HORIZONTAL_SPEED_MUL = 4.8;

/** Resting CENTRE Y of a cube spawned on the grid's first column. */
export function jumpGroundedCentreY(chart: { grid: JumpGrid; playerSize: number }): number {
  return jumpSpawnTopY(chart.grid) - chart.playerSize / 2;
}

const VALID_TILE_CHARS = new Set<string>(Object.values(JUMP_TILE));

export function validateJumpChart(chart: JumpChart): ChartValidationResult {
  const issues: ChartValidationIssue[] = [];

  validateBaseFields(chart, issues);

  if (!Number.isFinite(chart.gravity) || chart.gravity <= 0 || chart.gravity > 10000) {
    issues.push({ level: 'error', message: `gravity ${chart.gravity} must be in (0, 10000]` });
  }
  if (!Number.isFinite(chart.jumpVelocity) || chart.jumpVelocity <= 0 || chart.jumpVelocity > 5000) {
    issues.push({ level: 'error', message: `jumpVelocity ${chart.jumpVelocity} must be in (0, 5000]` });
  }
  if (
    !Number.isFinite(chart.shortJumpVelocity) ||
    chart.shortJumpVelocity <= 0 ||
    chart.shortJumpVelocity >= chart.jumpVelocity
  ) {
    issues.push({
      level: 'error',
      message: `shortJumpVelocity ${chart.shortJumpVelocity} must be in (0, jumpVelocity ${chart.jumpVelocity})`,
    });
  }
  if (Number.isFinite(chart.gravity) && Number.isFinite(chart.jumpVelocity) && chart.gravity > 0) {
    const airtime = jumpAirtimeBeats(chart.gravity, chart.jumpVelocity);
    if (!Number.isFinite(airtime) || airtime <= 0 || airtime > 8) {
      issues.push({
        level: 'error',
        message: `derived jump air-time ${airtime.toFixed(3)} beats must be in (0, 8]`,
      });
    }
  }
  if (!Number.isFinite(chart.playerSize) || chart.playerSize < 12 || chart.playerSize > 80) {
    issues.push({
      level: 'error',
      message: `playerSize ${chart.playerSize} out of range (12-80 playfield units)`,
    });
  }

  validateGrid(chart, issues);

  return {
    ok: !issues.some((i) => i.level === 'error'),
    issues,
  };
}

/** Structural integrity of the tile grid (clearability is the builder's filter). */
function validateGrid(chart: JumpChart, issues: ChartValidationIssue[]): void {
  const grid = chart.grid;
  if (!grid || typeof grid !== 'object') {
    issues.push({ level: 'error', message: 'Jump chart needs a grid' });
    return;
  }
  if (grid.rows !== JUMP_GRID_ROWS) {
    issues.push({ level: 'error', message: `grid.rows ${grid.rows} must equal ${JUMP_GRID_ROWS}` });
  }
  if (grid.cellPx !== JUMP_CELL_PX) {
    issues.push({ level: 'error', message: `grid.cellPx ${grid.cellPx} must equal ${JUMP_CELL_PX}` });
  }
  if (!Number.isFinite(grid.cellBeats) || grid.cellBeats <= 0) {
    issues.push({ level: 'error', message: `grid.cellBeats ${grid.cellBeats} must be > 0` });
  }
  if (!Number.isInteger(grid.cols) || grid.cols < 1 || grid.cols > JUMP_MAX_GRID_COLS) {
    issues.push({ level: 'error', message: `grid.cols ${grid.cols} must be an integer in [1, ${JUMP_MAX_GRID_COLS}]` });
    return;
  }
  if (!Array.isArray(grid.tiles) || grid.tiles.length !== grid.rows) {
    issues.push({ level: 'error', message: `grid.tiles must have exactly ${grid.rows} rows` });
    return;
  }
  for (let r = 0; r < grid.rows; r += 1) {
    const line = grid.tiles[r];
    if (typeof line !== 'string' || line.length !== grid.cols) {
      issues.push({ level: 'error', message: `grid.tiles[${r}] must be a ${grid.cols}-char string` });
      return;
    }
    for (const ch of line) {
      if (!VALID_TILE_CHARS.has(ch)) {
        issues.push({ level: 'error', message: `grid.tiles[${r}] has invalid tile '${ch}'` });
        return;
      }
    }
  }

  // Cover the chart: the grid must span at least totalBeats and not run far past.
  if (Number.isFinite(grid.cellBeats) && grid.cellBeats > 0 && Number.isFinite(chart.totalBeats)) {
    const span = grid.cols * grid.cellBeats;
    if (span < chart.totalBeats - 1e-6) {
      issues.push({ level: 'error', message: `grid spans ${span} beats, less than totalBeats ${chart.totalBeats}` });
    }
    if (span > chart.totalBeats + grid.cellBeats + 1e-6) {
      issues.push({ level: 'error', message: `grid spans ${span} beats, more than one column past totalBeats ${chart.totalBeats}` });
    }
  }

  validateColumnStructure(grid, issues);

  // Spawn column must be solid ground so the cube starts grounded.
  if (bottomCell(grid, 0) !== JUMP_TILE.floor) {
    issues.push({ level: 'error', message: 'grid column 0 must be solid floor (the spawn)' });
  }

  validateDecodedHazards(chart, issues);

  if (grid.hints) {
    for (const hint of grid.hints) {
      if (!Number.isInteger(hint.col) || hint.col < 0 || hint.col >= grid.cols) {
        issues.push({ level: 'error', message: `grid hint col ${hint.col} out of range` });
      }
      if (typeof hint.text !== 'string' || hint.text.length > JUMP_HINT_MAX_LENGTH) {
        issues.push({ level: 'error', message: `grid hint text must be ≤ ${JUMP_HINT_MAX_LENGTH} chars` });
      }
    }
  }
}

function bottomCell(grid: JumpGrid, col: number): string {
  return grid.tiles[grid.rows - 1]?.charAt(col) || JUMP_TILE.air;
}

/**
 * Enforce the single-surface-per-column contract: floor (#) connects to the
 * bottom, ceiling (=) connects to the top, with no orphan slabs floating in the
 * middle (the engine's landing math relies on one floor + one ceiling per column).
 */
function validateColumnStructure(grid: JumpGrid, issues: ChartValidationIssue[]): void {
  for (let col = 0; col < grid.cols; col += 1) {
    let floorBroken = false;
    for (let row = grid.rows - 1; row >= 0; row -= 1) {
      const ch = grid.tiles[row].charAt(col);
      if (ch === JUMP_TILE.floor) {
        if (floorBroken) {
          issues.push({ level: 'error', message: `grid col ${col} has a floating floor cell at row ${row}` });
          return;
        }
      } else {
        floorBroken = true;
      }
    }
    let ceilBroken = false;
    for (let row = 0; row < grid.rows; row += 1) {
      const ch = grid.tiles[row].charAt(col);
      if (ch === JUMP_TILE.ceiling) {
        if (ceilBroken) {
          issues.push({ level: 'error', message: `grid col ${col} has a floating ceiling cell at row ${row}` });
          return;
        }
      } else {
        ceilBroken = true;
      }
    }
  }
}

/** Hazards must be in-bounds and physically clearable by the constant arc. */
function validateDecodedHazards(chart: JumpChart, issues: ChartValidationIssue[]): void {
  const decoded = decodeJumpGrid(chart.grid);
  const playerHalf = chart.playerSize / 2;
  const peakLong = jumpPeakHeight(chart.gravity, chart.jumpVelocity);
  const peakShort = jumpPeakHeight(chart.gravity, chart.shortJumpVelocity);
  const groundedCentre = jumpGroundedCentreY(chart);
  const groundedTop = groundedCentre - playerHalf;

  for (const spike of decoded.spikes) {
    if (spike.beat < -1e-6 || spike.beat > chart.totalBeats + 1e-6) {
      issues.push({ level: 'error', message: `spike ${spike.id} at beat ${spike.beat} outside [0, ${chart.totalBeats}]`, beat: spike.beat });
    }
    const height = spike.baseY - spike.tipY;
    if (height > peakLong + playerHalf + 1e-6) {
      issues.push({
        level: 'error',
        message: `spike ${spike.id} height ${height.toFixed(1)} exceeds the clearable peak ${(peakLong + playerHalf).toFixed(1)}`,
        beat: spike.beat,
      });
    }
  }

  for (const saw of decoded.saws) {
    if (saw.beat < -1e-6 || saw.beat > chart.totalBeats + 1e-6) {
      issues.push({ level: 'error', message: `saw ${saw.id} at beat ${saw.beat} outside [0, ${chart.totalBeats}]`, beat: saw.beat });
    }
    // Clearable iff you can arc the cube's bottom above topY (jump over) OR keep
    // its top below bottomY while grounded (duck under). Jumping over is bounded
    // by the cube BOTTOM at the apex (`groundedCentre − peakLong + playerHalf`),
    // so the clear line is `saw.topY − playerHalf` vs the apex centre.
    const canJumpOver = saw.topY - playerHalf >= groundedCentre - peakLong - 1e-6;
    const canDuckUnder = saw.bottomY <= groundedTop + 1e-6;
    if (!canJumpOver && !canDuckUnder) {
      issues.push({ level: 'error', message: `saw ${saw.id} is neither jumpable-over nor duckable-under`, beat: saw.beat });
    }
  }

  for (const ov of decoded.overheads) {
    // The grounded cube must survive standing beneath every overhang.
    if (ov.bottomY > groundedTop + 1e-6) {
      issues.push({
        level: 'error',
        message: `overhead ${ov.id} underside ${ov.bottomY} is below the grounded cube top ${groundedTop.toFixed(1)}`,
        beat: ov.startBeat,
      });
    }
  }

  // A corridor (overhead above a forced short jump) must still let a short jump
  // fit beneath, else it is unpassable. Only flag overheads with a spike under.
  const shortApexTop = groundedTop - peakShort;
  for (const ov of decoded.overheads) {
    const spikeUnder = decoded.spikes.some(
      (s) => s.beat >= ov.startBeat - 1e-6 && s.beat <= ov.endBeat + 1e-6,
    );
    if (spikeUnder && shortApexTop < ov.bottomY - 1e-6) {
      issues.push({
        level: 'error',
        message: `corridor overhead ${ov.id} underside ${ov.bottomY} is too low — even a short jump's apex (top ${shortApexTop.toFixed(1)}) cracks it`,
        beat: ov.startBeat,
      });
    }
  }
}

function validateBaseFields(chart: JumpChart, issues: ChartValidationIssue[]): void {
  if (!chart.id) {
    issues.push({ level: 'error', message: 'Chart id is required' });
  }
  const title = chart.title ?? '';
  if (!title.trim()) {
    issues.push({ level: 'error', message: 'Chart title is required' });
  }
  if (title.length > CHART_LIMITS.maxTitleLength) {
    issues.push({ level: 'error', message: `Title exceeds ${CHART_LIMITS.maxTitleLength} chars` });
  }
  if ((chart.description ?? '').length > CHART_LIMITS.maxDescriptionLength) {
    issues.push({
      level: 'error',
      message: `Description exceeds ${CHART_LIMITS.maxDescriptionLength} chars`,
    });
  }
  if (chart.mapper !== undefined) {
    if (typeof chart.mapper !== 'string' || chart.mapper.trim().length === 0) {
      issues.push({ level: 'error', message: 'mapper, when present, must be a non-empty string' });
    } else if (chart.mapper.length > CHART_LIMITS.maxMapperLength) {
      issues.push({ level: 'error', message: `Mapper exceeds ${CHART_LIMITS.maxMapperLength} chars` });
    }
  }
  if (!Number.isFinite(chart.bpm) || chart.bpm < 40 || chart.bpm > 500) {
    issues.push({ level: 'error', message: `BPM ${chart.bpm} out of range (40-500)` });
  }
  if (!Number.isFinite(chart.totalBeats) || chart.totalBeats <= 0) {
    issues.push({ level: 'error', message: 'totalBeats must be > 0' });
  }
  if (Number.isFinite(chart.totalBeats) && chart.totalBeats > CHART_LIMITS.maxTotalBeats) {
    issues.push({
      level: 'error',
      message: `totalBeats ${chart.totalBeats} exceeds limit ${CHART_LIMITS.maxTotalBeats}`,
    });
  }
  if (!Number.isInteger(chart.difficulty) || chart.difficulty < 1 || chart.difficulty > 5) {
    issues.push({ level: 'error', message: `difficulty ${chart.difficulty} must be integer 1-5` });
  }
  if (!(VARIANTS as ReadonlyArray<string>).includes(chart.variant)) {
    issues.push({
      level: 'error',
      message: `variant ${chart.variant} must be one of ${VARIANTS.join(', ')}`,
    });
  }

  if (!chart.audio || typeof chart.audio.src !== 'string' || chart.audio.src.length === 0) {
    issues.push({ level: 'error', message: 'audio.src is required' });
  } else {
    if (!Number.isFinite(chart.audio.bpm) || chart.audio.bpm !== chart.bpm) {
      issues.push({
        level: 'error',
        message: `audio.bpm (${chart.audio.bpm}) must match chart.bpm (${chart.bpm})`,
      });
    }
    if (!Number.isFinite(chart.audio.offsetMs) || chart.audio.offsetMs < 0) {
      issues.push({ level: 'error', message: 'audio.offsetMs must be a non-negative number' });
    }
    if (!Number.isFinite(chart.audio.durationSec) || chart.audio.durationSec <= 0) {
      issues.push({ level: 'error', message: 'audio.durationSec must be > 0' });
    }
    if ((chart.audio.onsets?.length ?? 0) > CHART_LIMITS.maxOnsets) {
      issues.push({
        level: 'error',
        message: `audio.onsets ${chart.audio.onsets.length} exceeds limit ${CHART_LIMITS.maxOnsets}`,
      });
    }
  }

  for (const key of REQUIRED_PALETTE_KEYS) {
    const value = chart.palette?.[key];
    if (typeof value !== 'string' || !HEX_COLOUR_RE.test(value)) {
      issues.push({
        level: 'error',
        message: `palette.${key} must be a hex colour like #RRGGBB`,
      });
    }
  }
}
